import { doc, runTransaction } from "firebase/firestore";
import { produce } from "immer";
import { removeHierarchyAccount } from "./utils";

// Sensible empty defaults per hierarchy doc, used when the doc is missing so
// mutateFn always composes against a well-shaped object.
const EMPTY_DOCS = {
  accountsHierarchy: { types: [] },
  fundsHierarchy: { groups: [] },
  labelsHierarchy: { groups: [] },
};

/**
 * Transactional read-modify-write for organizationAndSettings hierarchy docs.
 *
 * Every hierarchy doc (accountsHierarchy / fundsHierarchy / labelsHierarchy) is
 * a single document the COA/funds/labels UIs used to overwrite wholesale from
 * client-held (stale) state. Because the old account-create transaction never
 * `transaction.get()`-ed the doc, Firestore could not detect conflicts and
 * concurrent writers silently lost entries (write-skew). This helper reads each
 * named doc INSIDE a `runTransaction` (Firestore requires all reads before any
 * write), hands the FRESH data to `mutateFn`, and writes back — so composition
 * always runs against current server data.
 *
 * Contract for `mutateFn(freshDocs, transaction)`:
 *   - `freshDocs` maps each requested docName → its fresh data (or the empty
 *     default when the doc does not exist). Compose ONLY on this — never on
 *     context/client copies.
 *   - `transaction` is the live transaction, passed so the mutate can create
 *     doc refs (`doc(collection)` generates an id locally, no read needed).
 *   - Return `{ writes, extraWrites? }`:
 *       • `writes`: `{ [docName]: newData }` — each is written back with
 *         `transaction.set` (full doc; shape unchanged). Only requested docs
 *         may be written.
 *       • `extraWrites` (optional): `(transaction) => void` for additional doc
 *         writes (account/fund docs) committed in the SAME transaction.
 *         `extraWrites` is the intended write seam for non-hierarchy docs;
 *         hierarchy docs go through `writes` so the unread-doc guard applies.
 *   - `mutateFn` may be async. Anything else it needs (e.g. a generated ref)
 *     should be captured via closure. The `transaction` param is rarely needed
 *     (mainly to generate a ref id) — writing hierarchy docs through it directly
 *     bypasses the unread-doc guard, so prefer `writes`/`extraWrites`.
 *
 * @param {import("firebase/firestore").Firestore} db
 * @param {string} org organization id
 * @param {string[]} docNames organizationAndSettings doc ids to read + write
 * @param {(freshDocs: Object, transaction: Object) => (Promise<{writes: Object, extraWrites?: Function}>|{writes: Object, extraWrites?: Function})} mutateFn
 * @returns {Promise<void>}
 */
export const updateHierarchyTx = (db, org, docNames, mutateFn) =>
  runTransaction(db, async (transaction) => {
    // Reads first — Firestore forbids any write before all reads in a tx.
    const refs = {};
    const freshDocs = {};
    for (const name of docNames) {
      const ref = doc(db, "orgs", org, "organizationAndSettings", name);
      refs[name] = ref;
      // eslint-disable-next-line no-await-in-loop -- sequential tx.get is fine
      const snap = await transaction.get(ref);
      freshDocs[name] = snap.exists()
        ? snap.data()
        : { ...(EMPTY_DOCS[name] || { groups: [] }) };
    }

    const { writes = {}, extraWrites } =
      (await mutateFn(freshDocs, transaction)) || {};

    for (const [name, data] of Object.entries(writes)) {
      if (!refs[name]) {
        throw new Error(
          `updateHierarchyTx: refusing to write unread doc "${name}"`,
        );
      }
      transaction.set(refs[name], data);
    }
    if (extraWrites) extraWrites(transaction);
  });

// Normalizes a hierarchy entry that may be a bare id string or a legacy
// object-form `{ id }` to its id.
const idOf = (entry) => (typeof entry === "string" ? entry : entry?.id);

// Every account id referenced by an accounts hierarchy (top-level + subAccounts).
const collectAccountIds = (hierarchy) => {
  const ids = new Set();
  (hierarchy?.types || []).forEach((type) =>
    (type.groups || []).forEach((group) =>
      (group.accounts || []).forEach((account) => {
        if (account?.id) ids.add(account.id);
        (account?.subAccounts || []).forEach((sub) => {
          const id = idOf(sub);
          if (id) ids.add(id);
        });
      }),
    ),
  );
  return ids;
};

/**
 * Reconcile a draft-derived accounts hierarchy (`final`) against the FRESH
 * hierarchy read inside the save transaction, so changes another writer made
 * while we were editing are neither lost nor resurrected:
 *
 *   - CONCURRENT CREATES — an id present in `fresh` but absent from `final`
 *     (and not one we just created) is re-inserted at its fresh placement:
 *     same type; same-named group if it exists in `final`, else that group is
 *     appended with just this account. Its subAccounts entries are preserved
 *     only where the sub id still exists in `fresh`.
 *   - CONCURRENT DELETES — an id in `final` but absent from BOTH `fresh` and
 *     `newIds` was deleted by another writer during our session and is removed
 *     from `final` (so we don't resurrect a ghost).
 *
 * Pure: `final` is not mutated; a new hierarchy object is returned.
 *
 * @param {Object} final draft-derived hierarchy ({ types: [...] }), draftId→realId
 *   already applied and ensureAllAccountsInHierarchy run.
 * @param {Object} fresh fresh accountsHierarchy doc data.
 * @param {string[]|Set<string>} newIds real ids created in THIS save (exempt
 *   from concurrent-delete detection).
 * @returns {Object} reconciled hierarchy ({ types: [...] }).
 */
export const reconcileHierarchies = (final, fresh, newIds) => {
  const newIdSet = newIds instanceof Set ? newIds : new Set(newIds || []);
  const freshIds = collectAccountIds(fresh);
  const finalIds = collectAccountIds(final);

  // Step 1 — re-insert concurrent creates at their fresh placement.
  let reconciled = produce(final, (draft) => {
    if (!draft.types) draft.types = [];
    const seen = new Set(finalIds);
    (fresh?.types || []).forEach((freshType) => {
      (freshType.groups || []).forEach((freshGroup) => {
        (freshGroup.accounts || []).forEach((freshAccount) => {
          const id = freshAccount?.id;
          if (!id || seen.has(id) || newIdSet.has(id)) return;

          const preservedSubs = (freshAccount.subAccounts || []).filter((sub) =>
            freshIds.has(idOf(sub)),
          );
          const entry = { id, subAccounts: preservedSubs };

          let type = draft.types.find((t) => t.type === freshType.type);
          if (!type) {
            type = { type: freshType.type, groups: [] };
            draft.types.push(type);
          }
          const group = type.groups.find(
            (g) => g.groupName === freshGroup.groupName,
          );
          if (group) group.accounts.push(entry);
          else
            type.groups.push({
              groupName: freshGroup.groupName,
              accounts: [entry],
            });
          seen.add(id);
        });
      });
    });
  });

  // Step 2 — drop ids we held that a concurrent writer deleted (never ours).
  const concurrentDeletes = [...finalIds].filter(
    (id) => !freshIds.has(id) && !newIdSet.has(id),
  );
  concurrentDeletes.forEach((id) => {
    reconciled = removeHierarchyAccount(reconciled, id);
  });

  return reconciled;
};

// Every item id referenced by a group-shaped hierarchy (funds/labels):
// top-level + sub-items.
const collectGroupIds = (hierarchy, itemsKey, subKey) => {
  const ids = new Set();
  (hierarchy?.groups || []).forEach((group) =>
    (group[itemsKey] || []).forEach((item) => {
      if (item?.id) ids.add(item.id);
      (item?.[subKey] || []).forEach((sub) => {
        const id = idOf(sub);
        if (id) ids.add(id);
      });
    }),
  );
  return ids;
};

// Pure removal of an item id from a group-shaped hierarchy, dropping now-empty
// groups (mirrors removeFundHierarchy for the generalized funds/labels shape).
const removeFromGroupHierarchy = (hierarchy, targetId, itemsKey, subKey) => {
  const groups = (hierarchy?.groups || [])
    .map((group) => {
      const items = (group[itemsKey] || [])
        .map((item) =>
          item[subKey]
            ? {
                ...item,
                [subKey]: item[subKey].filter((id) => idOf(id) !== targetId),
              }
            : { ...item },
        )
        .filter((item) => item.id !== targetId);
      return { ...group, [itemsKey]: items };
    })
    .filter((group) => (group[itemsKey] || []).length > 0);
  return { ...hierarchy, groups };
};

/**
 * Group-shaped ({ groups: [{ groupName, [itemsKey]: [...] }] }) analogue of
 * reconcileHierarchies, generalized for the funds (itemsKey "funds"/"subFunds")
 * and labels (itemsKey "labels"/"subLabels") hierarchies. Same concurrent
 * create / delete semantics; pure.
 *
 * @param {Object} final draft-derived group hierarchy.
 * @param {Object} fresh fresh hierarchy doc data.
 * @param {string} itemsKey "funds" | "labels".
 * @param {string} subKey "subFunds" | "subLabels".
 * @param {string[]|Set<string>} newIds ids created in THIS save (exempt).
 * @returns {Object} reconciled hierarchy ({ groups: [...] }).
 */
export const reconcileGroupHierarchies = (
  final,
  fresh,
  itemsKey,
  subKey,
  newIds,
) => {
  const newIdSet = newIds instanceof Set ? newIds : new Set(newIds || []);
  const freshIds = collectGroupIds(fresh, itemsKey, subKey);
  const finalIds = collectGroupIds(final, itemsKey, subKey);

  let reconciled = produce(final, (draft) => {
    if (!draft.groups) draft.groups = [];
    const seen = new Set(finalIds);
    (fresh?.groups || []).forEach((freshGroup) => {
      (freshGroup[itemsKey] || []).forEach((freshItem) => {
        const id = freshItem?.id;
        if (!id || seen.has(id) || newIdSet.has(id)) return;

        const preservedSubs = (freshItem[subKey] || []).filter((sub) =>
          freshIds.has(idOf(sub)),
        );
        const entry = { id, [subKey]: preservedSubs };

        const group = draft.groups.find(
          (g) => g.groupName === freshGroup.groupName,
        );
        if (group) {
          if (!group[itemsKey]) group[itemsKey] = [];
          group[itemsKey].push(entry);
        } else {
          draft.groups.push({
            groupName: freshGroup.groupName,
            [itemsKey]: [entry],
          });
        }
        seen.add(id);
      });
    });
  });

  const concurrentDeletes = [...finalIds].filter(
    (id) => !freshIds.has(id) && !newIdSet.has(id),
  );
  concurrentDeletes.forEach((id) => {
    reconciled = removeFromGroupHierarchy(reconciled, id, itemsKey, subKey);
  });

  return reconciled;
};
